// Speaker management (PRD 002 F2) + contact binding (docs/ideas.md). Inline
// rename ('S2' → 'Alice'), merge (when the diarizer over-split one person), and
// linking a speaker to a saved contact so their name/colour/avatar follow them
// across sessions. Rename + merge go through the store's mutable speakerId path,
// so every past segment updates with no text loss (AC2). Split is out of scope.

import { Merge, UserPlus, UserRound, X } from 'lucide-react'
import { useState } from 'react'
import { ContactAvatar } from '@/components/shell/ContactAvatar'
import { Button } from '@/components/ui/Button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/DropdownMenu'
import { Input } from '@/components/ui/Input'
import { upsertContact } from '@/lib/contacts/contactStore'
import { resolveIdentity, type Contact } from '@/lib/contacts/types'
import { cn } from '@/lib/utils'
import type { Speaker, SpeakerId } from '@/lib/transcription/types'

function SpeakerRow({
  speaker,
  others,
  contacts,
  contact,
  onRename,
  onMerge,
  onLink
}: {
  speaker: Speaker
  others: Speaker[]
  contacts: Contact[]
  contact?: Contact
  onRename: (id: SpeakerId, name: string) => void
  onMerge: (keep: SpeakerId, remove: SpeakerId) => void
  onLink: (id: SpeakerId, contactId: string | null) => void
}) {
  const [name, setName] = useState(speaker.displayName ?? '')
  const [merging, setMerging] = useState(false)
  const identity = resolveIdentity(speaker, contact)

  function commit() {
    if (name !== (speaker.displayName ?? '')) onRename(speaker.id, name)
  }

  // Create a contact from the speaker's current name/colour and link it.
  function createAndLink() {
    const label = (speaker.displayName ?? name).trim() || speaker.providerLabel
    const fresh: Contact = {
      id: crypto.randomUUID(),
      name: label,
      color: speaker.color,
      avatar: null,
      createdAt: Date.now()
    }
    void upsertContact(fresh)
    onLink(speaker.id, fresh.id)
  }

  return (
    <div className="rounded-md border border-border bg-surface p-2">
      <div className="flex items-center gap-2">
        <ContactAvatar
          name={identity.name}
          color={identity.color}
          avatar={identity.avatar}
          className="size-6 text-label"
        />
        {contact ? (
          <span className="flex min-w-0 flex-1 items-center gap-1.5">
            <span className={cn('truncate text-body-sm font-medium', identity.color)}>
              {contact.name}
            </span>
          </span>
        ) : (
          <Input
            value={name}
            placeholder={speaker.providerLabel}
            aria-label={`Name for ${speaker.providerLabel}`}
            onChange={(e) => setName(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') (e.target as HTMLInputElement).blur()
            }}
            className="h-7"
          />
        )}

        {/* link / unlink contact */}
        <DropdownMenu>
          <DropdownMenuTrigger
            render={
              <Button
                size="icon-xs"
                variant="ghost"
                aria-label={contact ? 'Change linked contact' : 'Link to a contact'}
              />
            }
          >
            <UserPlus className={cn(contact && identity.color)} />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            {contacts.length === 0 && (
              <DropdownMenuItem disabled>No saved contacts</DropdownMenuItem>
            )}
            {contacts.map((c) => (
              <DropdownMenuItem
                key={c.id}
                onClick={() => onLink(speaker.id, c.id === contact?.id ? null : c.id)}
              >
                <ContactAvatar
                  name={c.name}
                  color={c.color}
                  avatar={c.avatar}
                  className="size-4 text-[0.5rem]"
                />
                <span className="truncate">{c.name}</span>
                {c.id === contact?.id && <span className="ml-auto text-accent">✓</span>}
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={createAndLink}>
              <UserRound /> New contact from this speaker
            </DropdownMenuItem>
            {contact && (
              <DropdownMenuItem
                variant="destructive"
                onClick={() => onLink(speaker.id, null)}
              >
                <X /> Unlink
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>

        {others.length > 0 && (
          <Button
            size="icon-xs"
            variant="ghost"
            aria-label={`Merge ${speaker.providerLabel} into another speaker`}
            aria-pressed={merging}
            onClick={() => setMerging((m) => !m)}
          >
            {merging ? <X /> : <Merge />}
          </Button>
        )}
      </div>

      {merging && (
        <div className="mt-2 space-y-1 border-t border-border pt-2">
          <p className="text-label text-muted-foreground">Same person as…</p>
          <div className="flex flex-wrap gap-1">
            {others.map((o) => (
              <Button
                key={o.id}
                size="xs"
                variant="outline"
                onClick={() => {
                  onMerge(o.id, speaker.id)
                  setMerging(false)
                }}
              >
                <span className={cn('size-2 rounded-full bg-current', o.color)} />
                {o.displayName ?? o.providerLabel}
              </Button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export function SpeakerList({
  speakers,
  contacts,
  onRename,
  onMerge,
  onLink
}: {
  speakers: Record<SpeakerId, Speaker>
  contacts: Contact[]
  onRename: (id: SpeakerId, name: string) => void
  onMerge: (keep: SpeakerId, remove: SpeakerId) => void
  onLink: (id: SpeakerId, contactId: string | null) => void
}) {
  const list = Object.values(speakers)
  if (list.length === 0) {
    return (
      <p className="text-label text-muted-foreground">
        Speakers appear here as they're detected.
      </p>
    )
  }
  const byId = new Map(contacts.map((c) => [c.id, c]))
  return (
    <div className="space-y-2">
      {list.map((sp) => (
        <SpeakerRow
          key={sp.id}
          speaker={sp}
          others={list.filter((o) => o.id !== sp.id)}
          contacts={contacts}
          contact={sp.contactId ? byId.get(sp.contactId) : undefined}
          onRename={onRename}
          onMerge={onMerge}
          onLink={onLink}
        />
      ))}
    </div>
  )
}
